import type { Component, Entity2D, Vector2 } from '../../odie';
import { Container, Sprite } from '../../odie';

import { arrayCreate2 } from '../../lib/util/jsTools';
import { config } from '../defs/config';
import { MapDef } from '../defs/map';

// types
//-----------------------------------------------------------------------------
export type TileComponentOptions = {
  mapDef: MapDef;
  position: Vector2;
  tileMap: boolean[][];
};

// constants
//-----------------------------------------------------------------------------
const manifest = {
  center: 'tile.center.png',
  corner: 'tile.corner.png',
  edge: 'tile.edge.png',
  tip: 'tile.tip.png',
};

const ANGLE_0 = 0;
const ANGLE_1 = -Math.PI / 2;
const ANGLE_2 = -Math.PI;
const ANGLE_3 = -(Math.PI * 3) / 2;

/*
    tile component
*/
export class TileComponent implements Component {
  // constants
  //-------------------------------------------------------------------------
  public static readonly NAME = 'tile';

  // fields
  //-------------------------------------------------------------------------
  // injected
  public entity!: Entity2D;
  // scene
  public container!: Container;

  // impl
  //-------------------------------------------------------------------------
  public init(options: TileComponentOptions): void {
    // create container
    const container = (this.container = new Container());

    // get map from current position
    const map = options.tileMap;

    // if has center, only need center tile
    if (map[1][1]) {
      const center = Sprite.from(manifest.center);
      center.width = config.tile.size + 1; // Fix for 1px gap
      center.height = config.tile.size + 1; // Fix for 1px gap
      container.addChild(center);
      // else spawn corners
    } else {
      // top left
      this._spawnCorner(map[0][1], map[0][0], map[1][0], ANGLE_0);
      // top right
      this._spawnCorner(map[1][0], map[2][0], map[2][1], ANGLE_1);
      // bottom right
      this._spawnCorner(map[2][1], map[2][2], map[1][2], ANGLE_2);
      // bottom left
      this._spawnCorner(map[1][2], map[0][2], map[0][1], ANGLE_3);
      this.container.sortChildren();
    }
  }

  public static assets(): string[] {
    return Object.values(manifest);
  }

  // private: spawn
  //-------------------------------------------------------------------------
  private _spawnCorner(
    left: boolean,
    tip: boolean,
    right: boolean,
    angle: number,
  ) {
    // solve corner
    const corner = this._solveCornerImage(left, tip, right, angle);

    // if solved, spawn corner image
    if (corner) {
      this._spawnCornerImage(corner.image, corner.angle, corner.z);
    }
  }

  private _spawnCornerImage(image: string, angle: number, z: number) {
    // create sprite
    const sprite = Sprite.from(image);

    // anchor, position, and rotate
    sprite.anchorX = 0.5;
    sprite.anchorY = 0.5;
    sprite.x = config.tile.size / 2;
    sprite.y = config.tile.size / 2;
    sprite.rotation = angle;
    sprite.zIndex = z;
    sprite.width = config.tile.size + 1; // Fix for 1px gap
    sprite.height = config.tile.size + 1; // Fix for 1px gap

    // spawn sprite
    this.container.addChild(sprite);
  }

  // private: support
  //-------------------------------------------------------------------------
  public static createTileMap(
    mapDef: MapDef,
    position: Vector2,
  ): boolean[][] | null {
    const cells = mapDef.grid.cells;
    const map = arrayCreate2<boolean>(
      3,
      3,
      (column, row) =>
        !!cells[position.x + column - 1]?.[position.y + row - 1]?.enabled,
    );

    for (const row of map) {
      for (const value of row) {
        if (value) return map;
      }
    }
    return null;
  }

  private _solveCornerImage(
    left: boolean,
    tip: boolean,
    right: boolean,
    angle: number,
  ): { image: string; angle: number; z: number } | undefined {
    // if both left and right, use combined corner image
    if (left && right) {
      return { image: manifest.corner, angle, z: 1 };
      // else if left or right
    } else if (left || right) {
      // if left, rotate left
      if (left) {
        angle += Math.PI / 2;
      }
      return { image: manifest.edge, angle, z: -1 };
      // else if tip use tip
    } else if (tip) {
      return { image: manifest.tip, angle, z: 0 };
    }

    return undefined;
  }
}
